<script setup lang="ts">
import type { AffiliateProduct } from '~/composables/useAffiliateAPI'

const props = defineProps<{
  product: AffiliateProduct
  productTitle: string
  displayPrice: number
}>()

const { t, locale } = useI18n()
const localePath = useLocalePath()
const notice = ref('')
let noticeTimer: ReturnType<typeof setTimeout> | undefined

const referralUrl = computed(() => {
  if (!import.meta.client) return props.product.referralUrl
  const url = new URL(props.product.referralUrl, window.location.origin)
  url.searchParams.set('target', localePath(`/producto/${props.product.slug}`))
  return url.toString()
})
const hasCommission = computed(() => props.product.commissionType !== 'NONE')
const money = (value: number) => new Intl.NumberFormat(locale.value, {
  style: 'currency',
  currency: props.product.currency || 'EUR',
}).format(value || 0)
const estimatedCommission = computed(() => {
  if (props.product.commissionType === 'PERCENTAGE') {
    return Math.max(0, props.displayPrice * props.product.commissionValue / 100)
  }
  if (props.product.commissionType === 'FIXED_AMOUNT') {
    return Math.min(Math.max(0, props.product.commissionValue), Math.max(0, props.displayPrice))
  }
  return 0
})
const ruleLabel = computed(() => props.product.commissionType === 'PERCENTAGE'
  ? `${props.product.commissionValue}%`
  : money(props.product.commissionValue))

const notify = (message: string) => {
  notice.value = message
  if (noticeTimer) clearTimeout(noticeTimer)
  noticeTimer = setTimeout(() => { notice.value = '' }, 2500)
}
const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(referralUrl.value)
    notify(t('productPage.affiliate.copied'))
  } catch {
    notify(t('productPage.affiliate.copyFailed'))
  }
}
const share = async () => {
  if (!navigator.share) return copyUrl()
  try {
    await navigator.share({
      title: props.productTitle,
      text: t('productPage.affiliate.shareText', { product: props.productTitle }),
      url: referralUrl.value,
    })
  } catch (cause) {
    if (!(cause instanceof DOMException && cause.name === 'AbortError')) await copyUrl()
  }
}

onBeforeUnmount(() => { if (noticeTimer) clearTimeout(noticeTimer) })
</script>

<template>
  <section class="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 dark:border-emerald-800 dark:bg-emerald-950/20" aria-labelledby="affiliate-product-tools-title">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2 id="affiliate-product-tools-title" class="font-bold text-gray-900 dark:text-white">{{ t('productPage.affiliate.title') }}</h2>
      <span v-if="hasCommission" class="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
        {{ t('productPage.affiliate.estimated', { amount: money(estimatedCommission) }) }}
      </span>
    </div>
    <p v-if="hasCommission" class="mt-1 text-sm text-gray-600 dark:text-gray-300">
      {{ t('productPage.affiliate.commission', { rule: ruleLabel }) }}
    </p>
    <div v-else class="mt-1 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
      <strong>{{ t('productPage.affiliate.noCommissionTitle') }}</strong>
      {{ t('productPage.affiliate.noCommissionDescription') }}
    </div>
    <p class="mt-3 truncate text-xs text-gray-500" :title="referralUrl">{{ referralUrl }}</p>
    <div class="mt-3 flex flex-wrap gap-2">
      <button type="button" class="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700" @click="copyUrl">
        <UIcon :name="notice ? 'i-lucide-check' : 'i-lucide-copy'" class="size-4" />
        {{ notice || t('productPage.affiliate.copyUrl') }}
      </button>
      <button type="button" class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:border-brand-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200" @click="share">
        <UIcon name="i-lucide-share-2" class="size-4" />{{ t('productPage.affiliate.share') }}
      </button>
    </div>
    <p class="sr-only" aria-live="polite">{{ notice }}</p>
  </section>
</template>
